import { show } from "@ebay/nice-modal-react";
import type { useSingleton } from "@tippyjs/react";
import { useSetAtom } from "jotai/index";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { canv } from "@/canv/global";
import type { PresentationWidget } from "@/elements/Presentation/Presentation";
import PresentationHeaderIcon from "@/icons/presentation/PresentationHeaderIcon.svg?react";
import { headerActiveButtonAtom, presentationScreenVisibility } from "@/interface/AppStore";
import { BottomBarItem } from "@/interface/BottomBar/BottomRightBar";
import { MembersCount } from "@/interface/HeaderBar/RightBar/Presentation/MembersCount";
import { BuyProModal } from "@/interface/ui/modals/BuyProModal";
import { Tooltip } from "@/interface/ui/Tooltip";
import { selectionManager } from "@/tools/cursorTool";
import styles from "./PresentationButton.module.scss";

type PresentationButtonProps = {
    singletoneTarget: ReturnType<typeof useSingleton>[1];
    className?: string;
    disabledClass: string;
    isActive: boolean;
    activePresentationUID: string;
};

export const PresentationButton = ({
    singletoneTarget,
    className,
    disabledClass,
    isActive,
    activePresentationUID,
}: PresentationButtonProps) => {
    const { t } = useTranslation();
    const [hasActivePresentation, setHasActivePresentation] = useState(false);
    const [count, setCount] = useState(0);
    const setActiveHeaderButton = useSetAtom(headerActiveButtonAtom);
    const setIsVisible = useSetAtom(presentationScreenVisibility);
    const isFreeUser = !canv.getInitDataProp("isProfessional");

    const handleClick = () => {
        canv.appInterface.commentsWindow.close();
        canv.appInterface.voteWindow.close();

        if (activePresentationUID) {
            const presentation = canv.canvasController.FindByUID(
                activePresentationUID,
            ) as PresentationWidget;
            if (presentation) {
                presentation.join();
                selectionManager.clearSelected();
            }
            setIsVisible(true);
        } else {
            setActiveHeaderButton(isActive ? null : "presentation");
        }
    };

    const openFreeModal = () => {
        show(BuyProModal, { feature: "presentation" });
    };

    useEffect(() => {
        setHasActivePresentation(!!activePresentationUID);
        const presentation = canv.canvasController.FindByUID(
            activePresentationUID,
        ) as PresentationWidget;
        if (presentation) {
            setCount(presentation.viewerUIDs.length);
        }
    }, [activePresentationUID]);

    useEffect(() => {
        const handle = () => {
            const presentation = canv.canvasController.FindByUID(
                activePresentationUID,
            ) as PresentationWidget;
            if (presentation) {
                setCount(presentation.viewerUIDs.length);
            }
        };
        document.addEventListener("updatePresentationMembersCountMessage", handle);
        return () => {
            document.removeEventListener("updatePresentationMembersCountMessage", handle);
        };
    }, [activePresentationUID]);

    if (isFreeUser) {
        return (
            <div className={styles.buttonWrapper}>
                <BottomBarItem
                    active={isActive || hasActivePresentation}
                    className={className}
                    onClick={openFreeModal}
                >
                    <PresentationHeaderIcon className={disabledClass} />
                </BottomBarItem>
            </div>
        );
    }

    return (
        <Tooltip
            singleton={singletoneTarget}
            content={activePresentationUID ? t("presentation.join") : t("presentations.title")}
        >
            <div className={styles.buttonWrapper}>
                <BottomBarItem
                    active={isActive || hasActivePresentation}
                    className={className}
                    onClick={handleClick}
                >
                    <PresentationHeaderIcon />
                </BottomBarItem>
                {hasActivePresentation && <MembersCount count={count} />}
            </div>
        </Tooltip>
    );
};
